import React from 'react';
import { ChevronLeft, Plus16 } from 'shared/assets/icons';
import { GoalItemCard } from 'shared/components/ui/goal-item-card';
import { Icon } from 'shared/components/ui/icon';
import { Colors, IconButton, Sizes } from 'shared/components/ui/icon-button';
import { Paths } from 'shared/routes';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { replaceUrlParams } from 'shared/utils/replace-url-params';

import * as Styled from './category-list-item.style';
import { DragState, ReorderPositions, SideBarItem } from './types';

export type CategoryListItemProps = {
  item: SideBarItem;
  goals: Goal[];
  lifeAreas: LifeArea[];
  dragState: DragState;
  handleDragOver: (e: React.DragEvent, item: SideBarItem) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleDragLeave: () => void;
  handleDragStart: (e: React.DragEvent, goalId: string) => void;
  handleDragEnd: () => void;
  toggleCollapse: (id: ID) => void;
  collapsedItems: ID[];
  onCategoryAdd?: (id: ID) => void;
};

export const CategoryListItem: React.FC<CategoryListItemProps> = ({
  item,
  goals,
  lifeAreas,
  dragState,
  handleDragOver,
  handleDrop,
  handleDragLeave,
  handleDragStart,
  handleDragEnd,
  toggleCollapse,
  collapsedItems,
  onCategoryAdd,
}) => {
  const isCollapsed = !collapsedItems.includes(item.id);
  const onCollapse = () => toggleCollapse(item.id);
  const onDragOver = (e: React.DragEvent) => handleDragOver(e, item);

  if ('lifeArea' in item) {
    const isDropTarget = dragState.dropTargetId === item.id;
    const _onCategoryAdd = () => onCategoryAdd?.(item.id);

    return (
      <Styled.CategoryContainer>
        <Styled.CategoryRow
          onDragOver={onDragOver}
          onDrop={handleDrop}
          onDragLeave={handleDragLeave}
          $isDropTarget={
            isDropTarget &&
            dragState.dropPosition === ReorderPositions.InsideCategory
          }
        >
          <Styled.CategoryLinkContainer
            to={replaceUrlParams(Paths.LifeAreaDetail, { id: item.id })}
          >
            <Styled.CategoryTitle>{item.lifeArea.name}</Styled.CategoryTitle>

            <Styled.ChevronContainer>
              <Icon icon={ChevronLeft} />
            </Styled.ChevronContainer>
          </Styled.CategoryLinkContainer>

          <Styled.CategoryButtonsContainer>
            {!!onCategoryAdd && (
              <Styled.CategoryCreateButtonContainer>
                <IconButton
                  size={Sizes.Small}
                  icon={Plus16}
                  color={Colors.Default}
                  onClick={_onCategoryAdd}
                />
              </Styled.CategoryCreateButtonContainer>
            )}
            {item.hasGoals && (
              <IconButton
                size={Sizes.Small}
                icon={ChevronLeft}
                rotation={isCollapsed ? -90 : 0}
                color={Colors.Default}
                onClick={onCollapse}
              />
            )}
          </Styled.CategoryButtonsContainer>
        </Styled.CategoryRow>
      </Styled.CategoryContainer>
    );
  }

  const goal = item.goal;
  const isDropTarget = dragState.dropTargetId === goal.id;
  const isDragging = dragState.draggedId === goal.id;
  const hasSubGoals = item.hasGoals;
  const _onCollapse = hasSubGoals ? onCollapse : undefined;
  const onDragStart = (e: React.DragEvent) => handleDragStart(e, goal.id);

  return (
    <Styled.GoalListItem
      onDragOver={onDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
      $isDropTarget={
        isDropTarget && dragState.dropPosition === ReorderPositions.Inside
      }
      $isDragging={isDragging}
      $level={item.level}
    >
      {isDropTarget && dragState.dropPosition === ReorderPositions.Before && (
        <Styled.DropIndicator $position="top" />
      )}

      <Styled.GoalListItemInner
        draggable
        onDragStart={onDragStart}
        onDragEnd={handleDragEnd}
      >
        <GoalItemCard
          goal={goal}
          lifeAreas={lifeAreas}
          goals={goals}
          collapsed={isCollapsed}
          onCollapse={_onCollapse}
          showMissingDates
        />
      </Styled.GoalListItemInner>

      {isDropTarget && dragState.dropPosition === ReorderPositions.After && (
        <Styled.DropIndicator $position="bottom" />
      )}
    </Styled.GoalListItem>
  );
};
